import { useRef, useSyncExternalStore } from 'react';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { StructureSetId } from '~/domain/changes/ids';
import { domainTrace } from '~/domain/diagnostics/domainTrace';
import { createStructureSetDetailView } from '~/domain/projections/factories';
import { StructureSetDetailView } from '~/domain/projections/views';

interface CacheEntry {
    version: number;
    structureSetId: StructureSetId;
    view: StructureSetDetailView | undefined;
}

/** Stable no-op subscribe used when there is no active session. */
const NOOP_SUBSCRIBE = (): (() => void) => () => {};

/**
 * Subscribe to the detail view of a single structure set by its domain
 * id. Returns undefined when the id is undefined, there is no active
 * session, or no matching SS exists on the patient. Cached by
 * version + id; lookups only re-run when the change tracker bumps the
 * version.
 *
 * Tolerates a missing session — consumers such as StructureSetItem can
 * mount before PatientSessionProvider's effect creates the session.
 */
export function useStructureSetDetailView(
    id: StructureSetId | undefined
): StructureSetDetailView | undefined {
    const session = useSessionOrNull();
    const store = session?.context.store;
    const cacheRef = useRef<CacheEntry | null>(null);

    return useSyncExternalStore(store?.subscribe ?? NOOP_SUBSCRIBE, () => {
        if (id === undefined || !session) {
            return undefined;
        }
        const version = store!.getVersion();
        if (
            cacheRef.current &&
            cacheRef.current.version === version &&
            cacheRef.current.structureSetId === id
        ) {
            return cacheRef.current.view;
        }
        const ss = session.patient.getStructureSetById(id);
        const view = ss ? createStructureSetDetailView(ss) : undefined;
        if (view === undefined) {
            domainTrace.view('useStructureSetDetailView not-found', {
                id,
                version,
            });
        } else {
            domainTrace.view('useStructureSetDetailView miss', { id, version });
        }
        cacheRef.current = { version, structureSetId: id, view };
        return view;
    });
}
